// MobileDrawer.tsx — the <1024px off-canvas nav (task-5-brief.md Step 2; handoff README §1
// "Mobile (<1024px)" table for sizes/motion/dismiss; Dashboard Shell.dc.html's drawer markup
// for structure — its active-nav-bar height (22px) only shows up there, not in the table).
//
// Same three zones as Rail.tsx (brand + BotSwitcher, RailNav, the workspace+account menu),
// reused verbatim, at the `touch` size variant RailNav/the menu row grew for this task.
//
// Hand-rolled scrim + `<aside role="dialog">` rather than packages/ui's `dialog` (Radix): the
// handoff's motion spec is exact (220ms cubic-bezier(0.32,0.72,0,1) translateX, 150ms scrim
// fade) and needs to sit inside this app's prefers-reduced-motion handling; Radix Dialog ships
// its own keyframe-based enter/exit classes (see packages/ui/src/components/dialog.tsx's
// `data-[state=open]:animate-in fade-in-0 zoom-in-95`), which don't carry a reduced-motion
// switch of their own and would either fight or need overriding entirely to hit these numbers.
// A plain scrim + aside gives full control over the transition for about the same amount of
// code a Dialog override would need, at the cost of writing (not inheriting) the focus trap
// and body scroll lock below — a fair trade for a component this size, same call Rail.tsx made
// against shadcn's sidebar primitive.
import { useEffect, useRef, useState } from 'react';
import { XIcon } from 'lucide-react';
import { Link, useLocation, useParams } from 'react-router';
import { BrandLogo } from '@repo/ui/components/brand-logo';
import { Skeleton } from '@repo/ui/components/skeleton';
import type { useSession } from '../../lib/auth-client';
import { useBots } from '../../hooks/useBots';
import { useT } from '../../locales/context';
import { RAIL_BG } from './Rail';
import { BotSwitcher } from './BotSwitcher';
import { FeedbackTrigger } from './FeedbackDialog';
import { RailNav } from './RailNav';
import { UsageBlock } from './UsageBlock';
import { WorkspaceMenuRow } from './WorkspaceMenu';

type Session = NonNullable<ReturnType<typeof useSession>['data']>;

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
const TRANSITION_MS = 220;

/** Same three-zone skeleton shape as `RailSkeleton`, sized for the drawer's 288px/touch
 * dimensions — shown on the rare frame where `useBots` hasn't resolved yet when the drawer is
 * opened (in practice `Rail.tsx` has usually already warmed this exact query). */
function DrawerSkeleton() {
  return (
    <>
      <Skeleton className="mt-3.5 h-[66px] shrink-0 rounded-[10px]" />
      <div className="mt-[18px] flex flex-1 flex-col gap-2">
        {Array.from({ length: 8 }, (_, i) => (
          <Skeleton key={i} className="h-11 rounded-lg" />
        ))}
      </div>
      <Skeleton className="mt-3.5 h-24 shrink-0 rounded-[10px]" />
      {/* Merged workspace+account row, touch size (2026-08-21 rail review). */}
      <Skeleton className="mt-2 h-[50px] shrink-0 rounded-lg" />
    </>
  );
}

/** Fix-wave item 1 — same failure this drawer previously shared with `Rail.tsx` and
 * `MobileTopBar.tsx`: `!bots` alone can't tell "still loading" from "failed forever", so a
 * settled-but-failed `useBots()` used to render `DrawerSkeleton` (`aria-hidden`) on every open,
 * with the drawer's own workspace+account menu — the mobile visitor's ONLY other route to
 * sign-out below 1024px, since `MobileTopBar`'s avatar menu still works but a user might
 * reasonably look here first — never mounting. Visible (no `aria-hidden`) and keeps the menu
 * reachable. */
function DrawerError({ session, onSignOut }: { session: Session; onSignOut: () => void }) {
  const t = useT();
  return (
    <div role="alert" className="flex flex-1 flex-col">
      <div className="mt-[18px] flex flex-1 flex-col items-start gap-1 px-1.5">
        <p className="text-[13px] font-medium text-destructive">{t.shell.botsLoadError}</p>
        <p className="text-[12.5px] text-muted-foreground">{t.shell.reloadToRetry}</p>
      </div>
      <div className="mt-2 shrink-0">
        <WorkspaceMenuRow
          email={session.user.email}
          isAdmin={session.user.role === 'admin'}
          onSignOut={onSignOut}
          touch
        />
      </div>
    </div>
  );
}

export function MobileDrawer({
  open,
  onClose,
  session,
  onSignOut,
  triggerRef,
}: {
  open: boolean;
  onClose: () => void;
  session: Session;
  onSignOut: () => void;
  /** The hamburger (or avatar) button that opens this — focus always returns here on close,
   * regardless of which of `MobileTopBar`'s two triggers was used to open it. */
  triggerRef: React.RefObject<HTMLButtonElement | null>;
}) {
  const { botId: routeBotId } = useParams();
  const { data: bots, isPending, error } = useBots();
  const location = useLocation();
  const panelRef = useRef<HTMLDivElement>(null);
  const t = useT();

  // Mount/unmount is decoupled from the visual transform so both the open AND close motions
  // actually animate: `phase` stays 'entering'/'open' while the panel is in the DOM, and
  // 'closing' for the unmount delay, so the closing transition gets to finish before the node
  // is removed. `entering`→`open` reacts to a *changed prop*, which React's own docs treat as
  // a render-time adjustment (guarded by comparing against the previous render's `open`) rather
  // than an effect — an effect calling setState synchronously in its body is exactly what
  // `react-hooks/set-state-in-effect` (this repo's lint gate) forbids. The two timed
  // transitions below (`entering`→`open` next frame, `closing`→`closed` after the transition
  // duration) still need effects, but only to call setState from an animation-frame/timeout
  // *callback*, which is the legitimate "external system" use an effect is for.
  type Phase = 'closed' | 'entering' | 'open' | 'closing';
  const [phase, setPhase] = useState<Phase>(open ? 'entering' : 'closed');
  // "Storing information from previous renders" via state, not a ref (react-hooks/refs
  // forbids reading/writing a ref during render) — React's own docs use this exact shape for
  // adjusting state in response to a changed prop without an effect.
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    setPhase(open ? 'entering' : 'closing');
  }
  const rendered = phase !== 'closed';
  const entered = phase === 'open';

  useEffect(() => {
    if (phase === 'entering') {
      const raf = requestAnimationFrame(() => setPhase('open'));
      return () => cancelAnimationFrame(raf);
    }
    if (phase === 'closing') {
      const timer = setTimeout(() => setPhase('closed'), TRANSITION_MS);
      return () => clearTimeout(timer);
    }
  }, [phase]);

  // Body scroll lock for as long as the panel is at least partially visible (through the close
  // transition too, not just the logically-open window) — restores whatever the page's own
  // overflow was, rather than assuming it was the default.
  useEffect(() => {
    if (!rendered) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [rendered]);

  // Focus moves into the panel on open (dispatch a11y requirement). Doesn't wait on the
  // transition — keyboard/screen-reader users shouldn't be delayed by the 220ms slide.
  useEffect(() => {
    if (!open) return;
    const firstFocusable = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    firstFocusable?.focus();
  }, [open]);

  // ...and returns to the trigger that opened it once closed.
  useEffect(() => {
    if (open) return;
    triggerRef.current?.focus();
  }, [open, triggerRef]);

  // Escape dismiss rule, plus a Tab trap so content behind the scrim is never reachable by
  // keyboard while open (dispatch a11y requirement) — belt-and-suspenders with the sibling
  // app tree's `inert` (Protected.tsx), which already removes it from the tab order.
  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  // Route-change dismiss rule, general case (browser back/forward, a link inside the
  // switcher's popover or the account dropdown — both portaled outside the `onClose`-wrapped
  // nav below). Tapping a nav item is handled separately and synchronously below so the
  // destination is never revealed behind the panel for a frame; this effect is the backstop
  // for navigations this component didn't itself intercept, and necessarily runs one render
  // after the route already changed.
  const lastPathname = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname !== lastPathname.current) {
      lastPathname.current = location.pathname;
      onClose();
    }
  }, [location.pathname, onClose]);

  if (!rendered) return null;

  const currentBotId = routeBotId ?? bots?.[0]?.id;

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <div
        aria-hidden="true"
        onClick={onClose}
        className={`absolute inset-0 bg-black/50 transition-opacity duration-150 motion-reduce:transition-none ${
          entered ? 'opacity-100' : 'opacity-0'
        }`}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t.shell.navigationLabel}
        id="mobile-nav-drawer"
        className={`absolute inset-y-0 left-0 flex w-[288px] flex-col border-r border-border px-3 pt-3.5 pb-3 shadow-el-3 transition-transform duration-[220ms] ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none ${
          entered ? 'translate-x-0' : '-translate-x-full'
        } ${RAIL_BG}`}
      >
        <div className="flex h-[34px] shrink-0 items-center justify-between gap-2.5 pr-1 pl-1.5">
          <Link to="/" onClick={onClose} className="flex min-w-0 items-center">
            <BrandLogo size={26} />
          </Link>
          <button
            type="button"
            aria-label={t.shell.closeMenu}
            onClick={onClose}
            className="grid size-8 shrink-0 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35"
          >
            <XIcon size={16} strokeWidth={2} />
          </button>
        </div>

        {isPending ? (
          <DrawerSkeleton />
        ) : error || !bots ? (
          <DrawerError session={session} onSignOut={onSignOut} />
        ) : (
          <>
            <div className="mt-3.5 shrink-0">
              <BotSwitcher bots={bots} currentBotId={currentBotId} />
            </div>

            {/* Closes the drawer synchronously in the same click that starts the navigation
                (React batches both state updates from one event into a single render), so the
                destination is never revealed behind the panel for a frame — a `useEffect` on
                the route alone (as used above for the general case) would run one render after
                `Outlet` had already swapped, which is the exact pitfall the dispatch calls out. */}
            <nav className="mt-[18px] flex flex-1 flex-col overflow-y-auto" onClick={onClose}>
              <RailNav botId={currentBotId} touch />
            </nav>

            {/* Feedback above the usage block, mirroring Rail.tsx's order (Johnny, 2026-08-18):
                quota + account row stay visually adjacent. */}
            <div className="mt-3.5 shrink-0">
              <FeedbackTrigger touch />
            </div>

            {/* Unlike the nav above, a wrapper-level `onClick={onClose}` is NOT safe here — most
                of this card (the bar, the note, the pages row, the benefit line) is inert, so a
                blank tap would close the drawer with no navigation at all (fix round 1, item 1).
                `UsageBlock`'s own `onNavigate` prop fires only from its two `NavLink`s (plan name
                and CTA, both non-portaled, both going to `/billing`), giving the same
                synchronous close-before-navigate as the nav above without swallowing taps on the
                card's inert majority. */}
            <div className="mt-2 shrink-0">
              <UsageBlock touch onNavigate={onClose} />
            </div>

            <div className="mt-2 shrink-0">
              <WorkspaceMenuRow
                email={session.user.email}
                isAdmin={session.user.role === 'admin'}
                onSignOut={onSignOut}
                onNavigate={onClose}
                touch
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
